<template>
  <div>
    <PageHeaderBanner>
      <template #title>
        <span v-if="mode === DisplayModeCode.REGISTER">得意先および掛け率登録</span>
        <span v-if="mode === DisplayModeCode.CHANGE">得意先および掛け率変更</span>
      </template>
    </PageHeaderBanner>
    <MessageChip :display="isMessageChip" level="E" :message="errMessage" />
    <div class="cmd00410-container">
      <q-form
        greedy
        no-error-focus
        @submit.prevent="showRegisterConfirmDialog"
        @reset="clearData"
        @validation-error="resetError"
      >
        <div
          class="cmd00410-input-form"
          :style="mode === DisplayModeCode.CHANGE ? 'margin-top: -30px' : ''"
        >
          <div class="row">
            <div class="col flex justify-end" style="width: 100%">
              <q-toggle
                v-if="mode === DisplayModeCode.CHANGE"
                v-model="customerDataList.is_deleted"
                label="無効にする"
                left-label
                size="lg"
              />
            </div>
          </div>
          <div class="row q-gutter-x-sm">
            <div class="col-2">
              <label :class="mode === DisplayModeCode.REGISTER && 'requier'">得意先コード</label>
              <q-input
                v-if="mode === DisplayModeCode.REGISTER"
                v-model="customerDataList.customer_code"
                outlined
                maxlength="7"
                dense
                lazy-rules
                :error="customerCodeError"
                :rules="[
                  (val) => isEmpty(val) || $t('validation.required'),
                  (val) => isHalfAlphanumeric(val) || $t('validation.half_alphanumeric_only'),
                ]"
                style="background: var(--q-white)"
                class="input-area"
                @keydown.enter.prevent
              />
              <p v-if="mode === DisplayModeCode.CHANGE" class="q-mb-none q-ml-sm q-mt-sm">
                {{ customerDataList.customer_code }}
              </p>
            </div>
            <div class="col">
              <label class="requier">得意先名｜納品場所</label>
              <q-input
                v-model="customerDataList.customer_name"
                outlined
                maxlength="100"
                dense
                lazy-rules
                :rules="[(val) => isEmpty(val) || $t('validation.required')]"
                style="background: var(--q-white)"
                class="input-area"
                @keydown.enter.prevent
              />
            </div>
          </div>
        </div>
        <div class="row items-center cmd00410-table-upper">
          <label class="text-weight-bold" style="font-size: 1.1rem">掛け率情報</label>
          <div class="row items-center q-ml-xl q-gutter-x-sm rate-area">
            <label>掛け率</label>
            <q-input
              v-model="batchInputRate"
              outlined
              dense
              maxlength="7"
              :error="batchInputError"
              style="background: var(--q-white); width: 120px"
              @blur="batchInputRate === '' ? (batchInputError = false) : ''"
              @keydown.enter.prevent
            />
            <q-btn
              outline
              color="primary"
              icon="mdi-pencil"
              label="一括入力"
              :disabled="batchInputRate === ''"
              @click="pressBatchInputBtn"
            />
          </div>
        </div>

        <!-- 掛け率テーブル -->
        <div
          :class="
            'cmd00410-rate-table chem-table-area ' + (rateData.length === 0 ? 'table-nodata' : '')
          "
        >
          <q-table
            :rows="rateData"
            :columns="columns"
            row-key="supplier_code"
            flat
            dense
            separator="cell"
            :rows-per-page-options="[10, 20, 50, 0]"
            :hide-no-data="hideNoDataFlg"
            no-data-label="データがありません。"
            rows-per-page-label="件表示"
            :pagination-label="
              (firstRowIndex, endRowIndex, totalRowsNumber) => {
                return `${totalRowsNumber} 件中 ${firstRowIndex}～${endRowIndex} 件表示`;
              }
            "
          >
            <template #no-data="{ message }">
              <div class="full-width row q-gutter-sm">
                <q-icon size="24px" name="mdi-alert-circle-outline" />
                <span class="q-pt-xs">{{ message }}</span>
              </div>
            </template>
            <template #body="code">
              <q-tr v-if="!hideNoDataFlg" :props="code">
                <q-td>
                  {{ code.row.supplier_code }}
                </q-td>
                <q-td>
                  <p class="table-suplliername">
                    {{ code.row.supplier_name }}
                  </p>
                </q-td>
                <q-td v-for="(category, index) in code.row.category" :key="index">
                  <div class="table-input-area">
                    <q-input
                      v-model="category.rate"
                      outlined
                      dense
                      maxlength="7"
                      input-class="text-right"
                      lazy-rules
                      :error="category.error"
                      :error-message="category.error_message"
                      :rules="[
                        (val) =>
                          !!val ? isDecimalDigits(val) || $t('validation.half_numeric_only') : true,
                        (val) =>
                          !!val
                            ? parseInt(val, 10) < 10000 || $t('validation.max_four_integer')
                            : true,
                        (val) =>
                          !!val
                            ? isTwoDecimalNumeric(val) || $t('validation.two_decimal_places')
                            : true,
                      ]"
                      style="min-width: 100px; background: var(--q-white)"
                      @keydown.enter.prevent
                      @focus="category.error = false"
                    />
                  </div>
                </q-td>
              </q-tr>
            </template>
          </q-table>
        </div>
        <!-- ボタンエリア -->
        <FormFooterBanner style="position: inherit">
          <template #button-area>
            <div class="row">
              <q-btn
                color="grey-7"
                outline
                label="戻る"
                style="min-width: 120px; background: var(--q-white) !important"
                @click="backPage"
              />
              <div class="col q-gutter-x-xs flex justify-end">
                <q-btn
                  type="submit"
                  color="primary"
                  style="width: 150px"
                  :label="mode == DisplayModeCode.REGISTER ? '登録' : '変更'"
                />
                <q-btn
                  outline
                  type="reset"
                  style="
                    width: 150px;
                    background: var(--q-white) !important;
                    color: var(--q-delete);
                  "
                  :icon="mode == DisplayModeCode.REGISTER ? 'mdi-eraser' : 'cached'"
                  :label="mode == DisplayModeCode.REGISTER ? 'クリア' : 'リセット'"
                />
              </div>
            </div>
          </template>
        </FormFooterBanner>
      </q-form>
    </div>
    <!-- 登録ダイアログ -->
    <AlertDialog
      :display="isRegisterDialogFlg"
      :level="dialogLevel"
      :confirmation="isConfFlg"
      :title="dialogTitle"
      :text="dialogText"
      @complete="closeDialog"
      @confirm="submitData"
      @close="isRegisterDialogFlg = false"
    />
  </div>
</template>
<script setup lang="ts">
import { Loading } from 'quasar';
import { reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import ResultStatusCode from '../../common/code/ResultStatusCode';
import {
  isEmpty,
  isDecimalDigits,
  isHalfAlphanumeric,
  isTwoDecimalNumeric,
} from '../../common/utils/validateUtils';
import HttpResponseStatusCode from '../../common/code/HttpResponseStatusCode';
import DetailResultStatusCode from '../../common/code/DetailResultStatusCode';
import PageHeaderBanner from '../../components/banner/PageHeaderBanner.vue';
import FormFooterBanner from '../../components/banner/FormFooterBanner.vue';
import MessageChip from '../../components/chip/MessageChip.vue';
import AlertDialog from '../../components/dialogs/AlertDialog.vue';
import axios from '../../common/axios-common';
import DisplayModeCode from '../../common/code/DisplayModeCode';
import messages from '../../messages/message.json';

interface Props {
  // 登録・変更モード
  mode: string;
  // 得意先コード
  customerCd?: string;
}

// 掛け率のカテゴリー
interface Categories {
  category_id: string;
  rate: string;
  error: boolean;
  error_message: string;
}

// 掛け率マスタ
interface SupplierRate {
  supplier_code: string;
  supplier_name: string;
  category: Array<Categories>;
}

// 掛け率マスタ更新用
interface PriceFactor {
  supplier_code: string;
  category_id: string;
  rate: string;
}

const router = useRouter();
const props = defineProps<Props>();

// MessageChip表示フラグ
const isMessageChip = ref(false);
// エラーメッセージ
const errMessage = ref('');
// 得意先コードの相関チェックエラー時
const customerCodeError = ref(false);
// 一括入力エリア
const batchInputRate = ref('');
// 一括入力の入力値がエラーかどうか
const batchInputError = ref(false);
// Dialogタイトル
const dialogTitle = ref('');
// Dialog表示レベル
const dialogLevel = ref('');
// Dialog本文
const dialogText = ref(['']);
// 確認Dialogフラグ
const isConfFlg = ref(false);
// 登録Dialog表示フラグ
const isRegisterDialogFlg = ref(false);
// 得意先掛け率を更新していいかどうかのフラグ
const submitFlg = ref(true);
// 初期表示読み込み時「データがありません」を非表示にするためのフラグ(falseで表示)
const hideNoDataFlg = ref(true);

// 得意先初期化用データ
const customerDataFormat = reactive({
  customer_code: '',
  customer_name: '',
  is_deleted: false,
});

// 得意先入力データ
const customerDataList = ref({ ...customerDataFormat });

// 表示カラム
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const columns = ref<any>([]);
// テーブル表示データ
const rateData = ref<Array<SupplierRate>>([]);

// 更新画面リセット用
const resetDataList = ref<Array<string>>([]);

/**
 * エラーチップを初期化
 */
const resetError = async () => {
  errMessage.value = '';
  isMessageChip.value = false;
};

/**
 * 得意先掛け率新規登録 初期表示
 */
const registerInit = () => {
  Loading.show();

  axios
    .post('/cmd004101/search')
    .then((res) => {
      const { common, categoryList, supplierRateList } = res.data;
      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          // 掛け率テーブルのヘッダー情報（カテゴリー）を取得
          columns.value = categoryList;
          // 掛け率テーブルのサプライヤ情報を取得
          rateData.value = supplierRateList;
          break;

        // エラーの場合
        case ResultStatusCode.ERROR:
          router.push({ name: 'error' });
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      Loading.hide();
      hideNoDataFlg.value = false;
    });
};

/**
 * 得意先掛け率更新 初期表示
 */
const updateInit = () => {
  Loading.show();

  const formData = new FormData();
  formData.append('customer_code', props.customerCd ? props.customerCd : '');

  axios
    .post('/cmd004102/search', formData)
    .then((res) => {
      const { common, categoryList, supplierRateList, customerData } = res.data;

      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          // 掛け率テーブルのヘッダー情報（カテゴリー）を取得
          columns.value = categoryList;
          // 掛け率テーブルのサプライヤ情報を取得
          rateData.value = supplierRateList;
          // 得意先情報を取得
          customerDataList.value = customerData;
          // リセット用の得意先データを格納
          Object.assign(customerDataFormat, customerData);
          // リセット用データ作成
          customerDataList.value.is_deleted = customerData.is_deleted !== '0';
          customerDataFormat.is_deleted = customerDataList.value.is_deleted;

          for (let f = 0; f < rateData.value.length; f += 1) {
            for (let b = 0; b < rateData.value[f].category.length; b += 1) {
              resetDataList.value.push(rateData.value[f].category[b].rate);
            }
          }
          break;

        // エラーの場合
        case ResultStatusCode.ERROR:
          router.push({ name: 'error' });
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      Loading.hide();
      hideNoDataFlg.value = false;
    });
};

/**
 * rateDataの全てのrateのinputに一括入力inputの入力値を挿入
 */
const insertEachRateInput = () => {
  for (let i = 0; i < rateData.value.length; i += 1) {
    for (let t = 0; t < rateData.value[i].category.length; t += 1) {
      rateData.value[i].category[t].rate = Number(batchInputRate.value).toFixed(2);
      rateData.value[i].category[t].error = false;
      rateData.value[i].category[t].error_message = '';
    }
  }
};

/**
 * 一括入力ボタン押下時
 */
const pressBatchInputBtn = () => {
  // 一括入力欄を空にする
  errMessage.value = '';

  // エラーメッセージを非表示にする
  batchInputError.value = false;
  isMessageChip.value = false;

  // 入力値なしならチェックは行わない
  if (batchInputRate.value !== '') {
    if (!isDecimalDigits(batchInputRate.value)) {
      batchInputError.value = true;
      isMessageChip.value = true;
      errMessage.value = `${messages.ja.validation.half_numeric_only}`;
    } else if (parseInt(batchInputRate.value, 10) >= 10000) {
      batchInputError.value = true;
      isMessageChip.value = true;
      errMessage.value = `${messages.ja.validation.max_four_integer}`;
    } else if (!isTwoDecimalNumeric(batchInputRate.value)) {
      batchInputError.value = true;
      isMessageChip.value = true;
      errMessage.value = `${messages.ja.validation.two_decimal_places}`;
    } else {
      insertEachRateInput();
    }
  }
  window.scrollTo({ top: 0 });
};

/**
 * 登録確認ダイアログ表示
 */
const showRegisterConfirmDialog = () => {
  // 一括入力欄を空にする
  batchInputError.value = false;
  batchInputRate.value = '';

  if (props.mode === DisplayModeCode.REGISTER) {
    dialogTitle.value = '登録確認';
    dialogText.value = ['入力された内容で登録します。', 'よろしいですか？'];
  } else {
    dialogTitle.value = '更新確認';
    dialogText.value = ['入力された内容で更新します。', 'よろしいですか？'];
  }

  dialogLevel.value = 'information';
  isConfFlg.value = false;
  isRegisterDialogFlg.value = true;
  isMessageChip.value = false;
  errMessage.value = '';
};

/**
 * 掛け率の入力値チェック
 */
const checkEachRateInputValue = (inputValue: string) => {
  let error = false;
  let message = '';
  if (!isDecimalDigits(inputValue)) {
    error = true;
    message = `${messages.ja.validation.half_numeric_only}`;
    submitFlg.value = false;
  } else if (parseInt(inputValue, 10) >= 10000) {
    error = true;
    message = `${messages.ja.validation.max_four_integer}`;
    submitFlg.value = false;
  } else if (!isTwoDecimalNumeric(inputValue)) {
    error = true;
    message = `${messages.ja.validation.two_decimal_places}`;
    submitFlg.value = false;
  } else {
    error = false;
    message = '';
  }
  return { error, message };
};

/**
 * 得意先掛け率新規登録
 */
const registerInputValue = () => {
  Loading.show();
  isRegisterDialogFlg.value = false;
  const formData = new FormData();

  formData.append('customer_code', customerDataList.value.customer_code);
  formData.append('customer_name', customerDataList.value.customer_name);
  formData.append('is_deleted', customerDataList.value.is_deleted ? '1' : '0');

  const submitDataList = ref<Array<PriceFactor>>([]);
  // 掛け率入力値チェック
  submitFlg.value = true;
  for (let i = 0; i < rateData.value.length; i += 1) {
    for (let t = 0; t < rateData.value[i].category.length; t += 1) {
      const categoryPlace = rateData.value[i].category[t];
      // 入力値なしならControllerに渡さない
      if (categoryPlace.rate !== null && categoryPlace.rate !== '') {
        categoryPlace.rate = Number.isNaN(categoryPlace.rate)
          ? Number(categoryPlace.rate).toFixed(2)
          : categoryPlace.rate;
        // 入力値チェック結果を格納
        const checkResult = checkEachRateInputValue(categoryPlace.rate);
        if (checkResult.error) {
          categoryPlace.error = true;
          categoryPlace.error_message = checkResult.message;
        } else {
          const obj: PriceFactor = {
            supplier_code: rateData.value[i].supplier_code,
            category_id: categoryPlace.category_id,
            rate: categoryPlace.rate,
          };
          submitDataList.value.push(obj);
        }
      }
    }
  }

  // エラーがある場合は画面遷移を行わない
  if (submitFlg.value) {
    // 登録する掛け率情報設formにappend
    formData.append(`rateList`, JSON.stringify(submitDataList.value));

    axios
      .post('/cmd004101/register', formData)
      .then((res) => {
        const { common } = res.data;

        switch (common.status) {
          // 正常終了の場合
          case ResultStatusCode.SUCCESS:
            dialogTitle.value = '登録完了';
            dialogLevel.value = 'information';
            dialogText.value = ['登録が完了しました。'];
            isConfFlg.value = true;
            isRegisterDialogFlg.value = true;
            break;

          // エラーの場合
          case ResultStatusCode.ERROR:
            switch (common.detail_status) {
              case DetailResultStatusCode.DUPLICATE_CUSTOMER_ERROR:
              case DetailResultStatusCode.INSERT_ERROR:
                // 得意先コード入力エリアの枠を赤く（重複エラー）
                customerCodeError.value = true;
                // ダイアログ非表示
                isRegisterDialogFlg.value = false;
                isMessageChip.value = true;
                errMessage.value = [common.message].toString();
                break;

              // パラメータエラーの場合
              case DetailResultStatusCode.PARAMETER_ERROR:
                router.push({ name: 'error' });
                break;
              // システムエラーの場合
              case DetailResultStatusCode.SYSTEM_ERROR:
                router.push({ name: 'error' });
                break;
              default:
            }
            break;

          default:
            router.push({ name: 'error' });
        }
      })
      .catch((err) => {
        if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
          // セッションタイムアウトの場合
          router.push({ name: 'login', query: { timeout: 'timeout' } });
        } else {
          // その他エラーの場合
          router.push({ name: 'error' });
        }
      })
      .finally(() => {
        // Loading End
        Loading.hide();
        // TOPにスクロール
        setTimeout(() => window.scrollTo({ top: 0 }), 400);
      });
  } else {
    // Loading End
    Loading.hide();
    // ダイアログ非表示
    isRegisterDialogFlg.value = false;
    errMessage.value = messages.ja.message.MSG_50070E;
    isMessageChip.value = true;
    // TOPにスクロール
    setTimeout(() => window.scrollTo({ top: 0 }), 400);
  }
};

/**
 * 得意先掛け率更新
 */
const UpdateInputValue = () => {
  Loading.show();
  isRegisterDialogFlg.value = false;
  const formData = new FormData();

  formData.append('customer_code', customerDataList.value.customer_code);
  formData.append('customer_name', customerDataList.value.customer_name);
  formData.append('is_deleted', customerDataList.value.is_deleted ? '1' : '0');

  const submitDataList = ref<Array<PriceFactor>>([]);
  // 掛け率入力値チェック
  submitFlg.value = true;
  for (let i = 0; i < rateData.value.length; i += 1) {
    for (let t = 0; t < rateData.value[i].category.length; t += 1) {
      const categoryPlace = rateData.value[i].category[t];

      // 入力値なしならチェックは行わない
      if (categoryPlace.rate !== null && categoryPlace.rate !== '') {
        categoryPlace.rate = Number.isNaN(categoryPlace.rate)
          ? Number(categoryPlace.rate).toFixed(2)
          : categoryPlace.rate;
        // 入力値チェック結果を格納
        const checkResult = checkEachRateInputValue(categoryPlace.rate);
        if (checkResult.error) {
          categoryPlace.error = true;
          categoryPlace.error_message = checkResult.message;
        } else {
          const obj: PriceFactor = {
            supplier_code: rateData.value[i].supplier_code,
            category_id: categoryPlace.category_id,
            rate: categoryPlace.rate,
          };
          submitDataList.value.push(obj);
        }
      } else {
        const obj: PriceFactor = {
          supplier_code: rateData.value[i].supplier_code,
          category_id: categoryPlace.category_id,
          rate: categoryPlace.rate,
        };
        submitDataList.value.push(obj);
      }
    }
  }

  // エラーがある場合は画面遷移を行わない
  if (submitFlg.value) {
    // 更新する掛け率情報設formにappend
    formData.append(`rateList`, JSON.stringify(submitDataList.value));

    axios
      .post('/cmd004102/edit', formData)
      .then((res) => {
        const { common } = res.data;

        switch (common.status) {
          // 正常終了の場合
          case ResultStatusCode.SUCCESS:
            dialogTitle.value = '更新完了';
            dialogLevel.value = 'information';
            dialogText.value = ['更新が完了しました。'];
            // ダイアログを確認の状態に変更
            isConfFlg.value = true;
            // ダイアログを表示
            isRegisterDialogFlg.value = true;
            break;

          // エラーの場合
          case ResultStatusCode.ERROR:
            switch (common.detail_status) {
              case DetailResultStatusCode.UPDATE_ERROR:
                // ダイアログ非表示
                isRegisterDialogFlg.value = false;
                isMessageChip.value = true;
                errMessage.value = [common.message].toString();
                // TOPにスクロール
                setTimeout(() => window.scrollTo({ top: 0 }), 400);
                break;

              // パラメータエラーの場合
              case DetailResultStatusCode.PARAMETER_ERROR:
                router.push({ name: 'error' });
                break;
              // システムエラーの場合
              case DetailResultStatusCode.SYSTEM_ERROR:
                router.push({ name: 'error' });
                break;

              default:
            }
            break;
          default:
            router.push({ name: 'error' });
        }
      })
      .catch((err) => {
        if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
          // セッションタイムアウトの場合
          router.push({ name: 'login', query: { timeout: 'timeout' } });
        } else {
          // その他エラーの場合
          router.push({ name: 'error' });
        }
      })
      .finally(() => {
        Loading.hide();
        // TOPにスクロール
        setTimeout(() => window.scrollTo({ top: 0 }), 400);
      });
  } else {
    // Loading End
    Loading.hide();
    // ダイアログ非表示
    isRegisterDialogFlg.value = false;
    errMessage.value = messages.ja.message.MSG_50070E;
    isMessageChip.value = true;
    // TOPにスクロール
    setTimeout(() => window.scrollTo({ top: 0 }), 400);
  }
};

/**
 * 登録確認ダイアログのOK押下時
 */
const submitData = () => {
  Loading.show();

  // 得意先新規登録
  if (props.mode === DisplayModeCode.REGISTER) {
    registerInputValue();
  }
  // 得意先更新
  else {
    UpdateInputValue();
  }
};

/**
 * ダイアログのOKボタン押下時
 */
const closeDialog = () => {
  // エラーが無ければ得意先一覧画面に遷移
  if (dialogLevel.value === 'information') {
    router.replace({ name: 'customer' });
  } else {
    isRegisterDialogFlg.value = false;
  }
};

/**
 * 戻るボタン
 */
const backPage = () => {
  router.replace({ name: 'customer' });
};

/**
 * クリア（リセット）ボタン押下時
 */
const clearData = () => {
  // 得意先データ初期化
  customerDataList.value = { ...customerDataFormat };
  // 一括入力エリアの中身を初期化
  batchInputRate.value = '';
  // 一括入力欄を空にする
  errMessage.value = '';

  // エラーメッセージを非表示にする
  batchInputError.value = false;
  isMessageChip.value = false;

  if (props.mode === DisplayModeCode.REGISTER) {
    // 全部のデータの値を初期化
    for (let i = 0; i < rateData.value.length; i += 1) {
      for (let t = 0; t < rateData.value[i].category.length; t += 1) {
        rateData.value[i].category[t].rate = '';
        rateData.value[i].category[t].error = false;
        rateData.value[i].category[t].error_message = '';
      }
    }
  } else {
    // 全部のデータの値を初期表示のデータで上書き
    let roopCnt = 0;
    for (let i = 0; i < rateData.value.length; i += 1) {
      for (let t = 0; t < rateData.value[i].category.length; t += 1) {
        rateData.value[i].category[t].rate = resetDataList.value[roopCnt];
        rateData.value[i].category[t].error = false;
        rateData.value[i].category[t].error_message = '';
        roopCnt += 1;
      }
    }
  }
};

/**
 * 初期表示
 */
// propsが存在しない場合
if (!props.mode) {
  // 前画面に遷移
  backPage();
} else if (props.mode === DisplayModeCode.REGISTER) {
  // 得意先掛け率新規登録
  registerInit();
} else {
  // 得意先掛け率更新
  updateInit();
}
</script>
<style scoped>
.cmd00410-container {
  width: 90%;
  margin: 0 auto;
}
.cmd00410-input-form {
  margin-bottom: 20px;
}

.rate-area .q-btn:disabled {
  background-color: var(--q-opacityDisable) !important;
  border-color: var(--q-opacityDisable) !important;
  color: #fff !important;
}

.table-suplliername {
  text-overflow: ellipsis;
  margin: 0;
  max-width: 280px;
  overflow: hidden;
  white-space: nowrap;
}
</style>
<style>
.cmd00410-rate-table .q-table__container {
  margin: 10px auto !important;
}
.cmd00410-rate-table th:first-child,
.cmd00410-rate-table td:first-child {
  background: var(--q-white);
  position: sticky;
  left: 0;
  z-index: 1;
}
.cmd00410-rate-table th:nth-child(2),
.cmd00410-rate-table td:nth-child(2) {
  background: var(--q-white);
  position: sticky;
  left: 112px;
  z-index: 1;
}

.cmd00410-table-upper .rate-area .q-field--dense .q-field__control {
  height: 36px;
}

.cmd00410-table-upper .rate-area .q-field--with-bottom {
  padding-bottom: 0;
}

.cmd00410-table-upper .rate-area .q-field__marginal {
  height: 36px;
}

.cmd00410-table-upper .rate-area .q-btn--outline:disabled:before {
  border: none;
}

.cmd00410-table-upper .rate-area .q-field__native {
  text-align: right;
}

.cmd00410-table-upper
  .rate-are
  .chem-table-area.cmd00410-rate-table
  .q-table--dense
  .q-table
  tbody
  td {
  padding: 8px;
}

.cmd00410-rate-table.chem-table-area .q-table__middle .q-table tbody .q-td {
  padding: 0 8px;
  height: inherit;
}

.cmd00410-rate-table.chem-table-area .q-table .q-td .table-input-area {
  height: 100%;
  padding: 8px 0;
}

.cmd00410-rate-table .q-field.q-field--with-bottom {
  padding-bottom: 0;
}

.cmd00410-rate-table .q-field__bottom {
  position: static;
  padding: 0;
  min-height: auto;
}

.cmd00410-rate-table .q-field__messages {
  margin-top: -4px;
}

.cmd00410-rate-table.chem-table-area tbody .q-field--dense .q-field__marginal {
  height: 40px !important;
}
</style>
